import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ChatPanel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SUGGESTED_PROMPTS = [
  'What is the overall health of the factory?',
  'Show me all components with warning or critical status',
  'Which machines are in the outer ring production line?',
  'Trace the dependencies of component C-131',
  'What recent alerts have been triggered in the last hour?',
  'Which component has the highest temperature reading?',
];

const ToolCallBadge = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const isResult = event.type === 'tool_result';
  return (
    <div className={`tool-badge${isResult ? ' result' : ''}`}>
      <button className="tool-badge-header" onClick={() => setExpanded(e => !e)}>
        <span className="tool-badge-icon">{isResult ? '✓' : '⚙'}</span>
        <span className="tool-badge-name">
          {isResult ? `Result: ${event.tool}` : `Calling: ${event.tool}`}
        </span>
        <span className="tool-badge-chevron">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <pre className="tool-badge-detail">
          {isResult
            ? event.preview
            : JSON.stringify(event.input, null, 2)}
        </pre>
      )}
    </div>
  );
};

const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <div className={`message-row ${isUser ? 'user' : 'assistant'}`}>
      {!isUser && (
        <div className="avatar assistant-avatar">
          <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
            <polygon points="12,2 20,7 20,17 12,22 4,17 4,7" fill="url(#chatGrad)" opacity="0.9"/>
            <circle cx="12" cy="12" r="3" fill="#fff" opacity="0.9"/>
            <defs>
              <linearGradient id="chatGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4299e1"/>
                <stop offset="100%" stopColor="#9f7aea"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
      )}
      <div className="message-content">
        {message.events && message.events.map((ev, i) => {
          if (ev.type === 'tool_call' || ev.type === 'tool_result') {
            return <ToolCallBadge key={i} event={ev} />;
          }
          return null;
        })}
        {message.text && (
          <div className="message-bubble">
            <FormattedText text={message.text} />
          </div>
        )}
        {message.streaming && !message.text && (
          <div className="message-bubble typing">
            <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
          </div>
        )}
      </div>
      {isUser && <div className="avatar user-avatar">U</div>}
    </div>
  );
};

// Minimal markdown-like renderer (bold, code, bullet lists)
const FormattedText = ({ text }) => {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(<ul key={i}>{items.map((item, j) => <li key={j}><InlineText text={item}/></li>)}</ul>);
    } else if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      elements.push(<ol key={i}>{items.map((item, j) => <li key={j}><InlineText text={item}/></li>)}</ol>);
    } else if (line.startsWith('```')) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      elements.push(<pre key={i} className="inline-code-block"><code>{codeLines.join('\n')}</code></pre>);
    } else if (line.startsWith('### ')) {
      elements.push(<h4 key={i}>{line.slice(4)}</h4>);
      i++;
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={i}>{line.slice(3)}</h3>);
      i++;
    } else if (line === '') {
      elements.push(<br key={i} />);
      i++;
    } else {
      elements.push(<p key={i}><InlineText text={line} /></p>);
      i++;
    }
  }
  return <>{elements}</>;
};

const InlineText = ({ text }) => {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('`') && part.endsWith('`')) {
          return <code key={i} className="inline-code">{part.slice(1, -1)}</code>;
        }
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </>
  );
};

const ChatPanel = ({ telemetryData = [] }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Fetch backend status on mount
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/chat/status`)
      .then(r => r.json())
      .then(s => setStatus(s))
      .catch(() => setStatus(null));
  }, []);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const buildApiMessages = useCallback(() => {
    return messages
      .filter(m => m.role === 'user' || (m.role === 'assistant' && m.text))
      .map(m => ({ role: m.role, content: m.text || '' }));
  }, [messages]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', text: text.trim(), id: Date.now() };
    const assistantMsg = {
      role: 'assistant',
      text: '',
      events: [],
      streaming: true,
      id: Date.now() + 1,
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setInput('');
    setLoading(true);

    const apiMessages = [
      ...buildApiMessages(),
      { role: 'user', content: text.trim() },
    ];

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const resp = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            const event = JSON.parse(raw);
            setMessages(prev => {
              const updated = [...prev];
              const last = { ...updated[updated.length - 1] };
              if (event.type === 'text') {
                last.text = (last.text || '') + event.content;
              } else if (event.type === 'tool_call' || event.type === 'tool_result') {
                last.events = [...(last.events || []), event];
              } else if (event.type === 'done') {
                last.streaming = false;
              } else if (event.type === 'error') {
                last.text = `⚠ ${event.message}`;
                last.streaming = false;
              }
              updated[updated.length - 1] = last;
              return updated;
            });
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev => {
          const updated = [...prev];
          const last = { ...updated[updated.length - 1] };
          last.text = `⚠ Connection error: ${err.message}. Make sure the backend is running at ${BACKEND_URL}`;
          last.streaming = false;
          updated[updated.length - 1] = last;
          return updated;
        });
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  }, [loading, buildApiMessages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleStop = () => {
    abortRef.current?.abort();
    setLoading(false);
    setMessages(prev => {
      const updated = [...prev];
      const last = { ...updated[updated.length - 1] };
      last.streaming = false;
      if (!last.text) last.text = '*(stopped)*';
      updated[updated.length - 1] = last;
      return updated;
    });
  };

  const handleClear = () => {
    if (loading) abortRef.current?.abort();
    setMessages([]);
    setLoading(false);
  };

  return (
    <div className="chat-panel">
      {/* Header */}
      <div className="chat-header">
        <div className="chat-header-left">
          <svg viewBox="0 0 24 24" fill="none" width="22" height="22" className="chat-logo">
            <polygon points="12,2 20,7 20,17 12,22 4,17 4,7" fill="url(#hGrad)" opacity="0.9"/>
            <circle cx="12" cy="12" r="3" fill="#fff" opacity="0.85"/>
            <defs>
              <linearGradient id="hGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4299e1"/>
                <stop offset="100%" stopColor="#9f7aea"/>
              </linearGradient>
            </defs>
          </svg>
          <div>
            <h2>Digital Twin Assistant</h2>
            <span className="chat-subtitle">Powered by Claude Opus · Neptune Graph</span>
          </div>
        </div>
        <div className="chat-header-right">
          {status && (
            <div className="status-indicators">
              <span className={`status-dot ${status.neptune?.connected ? 'green' : 'amber'}`}
                title={status.neptune?.connected ? 'Neptune connected' : 'Neptune offline — using fallback graph'} />
              <span className="status-label">
                {status.neptune?.connected ? 'Neptune' : 'Local Graph'}
              </span>
              <span className={`status-dot ${status.claude?.api_key_set ? 'green' : 'red'}`}
                title={status.claude?.api_key_set ? 'Claude API ready' : 'No API key set'} />
              <span className="status-label">Claude</span>
            </div>
          )}
          <button className="chat-clear-btn" onClick={handleClear} title="Clear conversation">
            ✕ Clear
          </button>
        </div>
      </div>

      {/* Message list */}
      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-welcome">
            <div className="welcome-icon">
              <svg viewBox="0 0 48 48" fill="none" width="48" height="48">
                <polygon points="24,4 40,14 40,34 24,44 8,34 8,14" fill="url(#wGrad)" opacity="0.8"/>
                <circle cx="24" cy="24" r="7" fill="#fff" opacity="0.85"/>
                <defs>
                  <linearGradient id="wGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#4299e1"/>
                    <stop offset="100%" stopColor="#9f7aea"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h3>Ask about your factory</h3>
            <p>I can query the knowledge graph, retrieve live sensor readings,
               trace component dependencies, and surface alerts.</p>
            <div className="suggested-prompts">
              {SUGGESTED_PROMPTS.map((p, i) => (
                <button key={i} className="suggested-btn" onClick={() => sendMessage(p)}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map(msg => <MessageBubble key={msg.id} message={msg} />)}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form className="chat-input-row" onSubmit={handleSubmit}>
        <textarea
          ref={inputRef}
          className="chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage(input);
            }
          }}
          placeholder="Ask about components, alerts, dependencies…  (Enter to send)"
          rows={1}
          disabled={loading}
        />
        {loading ? (
          <button type="button" className="chat-send-btn stop" onClick={handleStop}>
            ⏹ Stop
          </button>
        ) : (
          <button type="submit" className="chat-send-btn" disabled={!input.trim()}>
            Send ↵
          </button>
        )}
      </form>
    </div>
  );
};

export default ChatPanel;
